import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const DashboardNotificationsContext = createContext(null);

const READ_KEY = "db_notif_read_ids";

function getReadSet() {
  try { return new Set(JSON.parse(localStorage.getItem(READ_KEY) || "[]")); }
  catch { return new Set(); }
}

function saveReadSet(set) {
  try { localStorage.setItem(READ_KEY, JSON.stringify([...set])); } catch {}
}

export function DashboardNotificationsProvider({ children }) {
  const [rawNotifications, setRawNotifications] = useState([]);
  const [readIds, setReadIds] = useState(getReadSet);
  const [loading, setLoading] = useState(false);
  const token =
    localStorage.getItem("authToken") || sessionStorage.getItem("authToken");

  const fetchNotifications = useCallback(async () => {
    if (!token || token.trim() === "") {
      setRawNotifications([]);
      return;
    }
    setLoading(true);
    try {
      const [ordersRes, messagesRes] = await Promise.all([
        axios.get("/orders", {
          params: { limit: 15 },
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: { orders: [] } })),
        axios.get("/messages", {
          params: { limit: 50 },
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: { messages: [] } })),
      ]);

      const orders = ordersRes?.data?.orders ?? [];
      const messages = messagesRes?.data?.messages ?? [];
      const unreadMessages = messages.filter((m) => !m.isRead);

      const items = [];

      orders.slice(0, 10).forEach((order) => {
        items.push({
          id: `order-${order._id}`,
          type: "order",
          title: "New order",
          senderName: order.customer?.fullName || "Customer",
          preview: `Order #${String(order._id).slice(-6).toUpperCase()}`,
          text: `Order from ${order.customer?.fullName || "Customer"}`,
          link: `/dashboard/orders/${order._id}`,
          createdAt: order.createdAt,
        });
      });

      unreadMessages.slice(0, 10).forEach((msg) => {
        items.push({
          id: `message-${msg._id}`,
          type: "message",
          title: "New message",
          senderName: msg.name || "Visitor",
          preview: (msg.message || "").slice(0, 70),
          text: `${msg.name}: ${(msg.message || "").slice(0, 50)}`,
          link: "/dashboard/messages",
          createdAt: msg.createdAt,
        });
      });

      items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setRawNotifications(items.slice(0, 20));
    } catch {
      setRawNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAsRead = useCallback((id) => {
    setReadIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      saveReadSet(next);
      return next;
    });
  }, []);

  const markAllAsRead = useCallback(() => {
    setReadIds((prev) => {
      const next = new Set(prev);
      rawNotifications.forEach((n) => next.add(n.id));
      saveReadSet(next);
      return next;
    });
  }, [rawNotifications]);

  const notifications = rawNotifications.map((n) => ({
    ...n,
    read: readIds.has(n.id),
  }));

  const unreadCount = notifications.filter((n) => !n.read).length;

  const value = {
    notifications,
    unreadCount,
    loading,
    refresh: fetchNotifications,
    markAsRead,
    markAllAsRead,
  };

  return (
    <DashboardNotificationsContext.Provider value={value}>
      {children}
    </DashboardNotificationsContext.Provider>
  );
}

export function useDashboardNotifications() {
  const ctx = useContext(DashboardNotificationsContext);
  if (!ctx) {
    throw new Error("useDashboardNotifications must be used within DashboardNotificationsProvider");
  }
  return ctx;
}
